import { Component, Element, Event, EventEmitter, h, Host, Prop, State } from "@stencil/core";

/**
 * @slot (default) - insert a standard, valid HTML video element
 */

@Component({
    tag: "ux-video",
    styleUrl: "video.scss",
    shadow: true
})
export class VideoPlayer {
    /**
     * Primary element reference
     */
    @Element() element!: HTMLUxVideoElement;

    /**
     * Control usage of custom play button
     * Will be set automatically if on of the parents has the class,b `js-video-play-button-handler`
     * meaning that the video has a custom overlay
     * @default false
     */
    @Prop({ reflect: true }) readonly noCustomPlayButton = false;

    /**
     * Event emitted when video controls are changed
     * @event uxVideoControlChange
     */
    @Event({
        eventName: "uxVideoControlChange",
        composed: true,
        cancelable: true,
        bubbles: true
    })
    private videoControlChange!: EventEmitter<{
        type: "play" | "pause" | "ended" | "volumechange";
        element: HTMLVideoElement;
    }>;

    @State() isInitialized = false;
    @State() isPaused = true;
    @State() isEnded = false;
    @State() hasVideoControls = false;
    @State() hasOwnPoster = false;
    @State() hasLoopAutoplay = false;
    @State() hasAutoplayNoControls = false;
    @State() hasIntercepted = false;

    private baseClass = "ux-video";
    private videoElement: HTMLVideoElement;
    private videoWrapper: HTMLDivElement;
    private haveControlsBeenSet = false;
    private isBrowserSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
    private autoplayAttr = "data-autoplay";

    /**
     * Safari does not load the first frame of the video as poster with preload="metadata"
     */
    private preloadAttribute = this.isBrowserSafari ? "auto" : "metadata";

    private handleIntersection = (entries: IntersectionObserverEntry[]) => {
        entries.map((entry) => {
            if (entry.isIntersecting) {
                this.onVideoIntersecting();
                if (this.videoElement.hasAttribute(this.autoplayAttr)) {
                    if (this.videoElement.hasAttribute("loop") || !this.hasIntercepted) {
                        this.videoElement.muted = true;
                        this.videoElement.play();
                    }
                    this.hasIntercepted = true;
                }
            } else {
                if (!this.isPaused && this.videoElement.hasAttribute("loop")) {
                    this.videoElement.pause();
                    this.handlePause();
                }
            }
        });
    };
    private intersectionObserver = new IntersectionObserver(this.handleIntersection);

    private onVideoIntersecting = () => {
        if (this.videoElement.getAttribute("preload") === "none") {
            if (this.videoElement.videoWidth === undefined) {
                // Only set minHeight if metadata is not yet loaded.
                // The "loadedmetadata" event listener is attached in the connectedCallback.
                this.videoWrapper.style.minHeight = `${this.videoWrapper.clientHeight}px`;
            }
            this.videoElement.setAttribute("preload", this.preloadAttribute);
            if (this.isBrowserSafari) {
                if (!this.hasOwnPoster) {
                    const source = this.videoElement.querySelector("source");
                    // @ts-expect-error Check for typing
                    const srcAttribute = source.getAttribute("src");
                    // @ts-expect-error Check for typing
                    source.setAttribute("src", srcAttribute + "#t=0.1");
                    this.videoElement.load();
                }
            }
        }
        if (!this.hasOwnPoster && this.videoElement.hasAttribute("poster")) {
            this.videoElement.removeAttribute("poster");
        }
    };

    /** Mutation observer */
    private contentObserver: MutationObserver = new MutationObserver(() => this.onWrapperClassChange());

    private onWrapperClassChange = () => {
        if (this.videoWrapper.classList.contains("is-videoPlaying")) {
            // @ts-expect-error Check for typing
            this.element.parentElement.classList.add("is-videoPlaying");
            if (!this.haveControlsBeenSet && this.hasVideoControls) {
                this.videoElement.setAttribute("controls", "");
                this.haveControlsBeenSet = true;
            }
        } else {
            // @ts-expect-error Check for typing
            this.element.parentElement.classList.remove("is-videoPlaying");
        }
    };

    private handleMetadata = (): void => {
        this.videoWrapper.removeAttribute("style");
    };

    private handlePlay = (): void => {
        this.videoControlChange.emit({
            type: "play",
            element: this.videoElement
        });
        this.isPaused = false;
        if (this.isEnded) {
            setTimeout(() => (this.isEnded = false), 500);
        }
        if (this.haveControlsBeenSet) {
            return;
        }
        if (this.hasVideoControls) {
            this.videoElement.setAttribute("controls", "");
            this.haveControlsBeenSet = true;
        }
    };

    private handlePause = (): void => {
        this.videoControlChange.emit({
            type: "pause",
            element: this.videoElement
        });
        this.isPaused = true;
    };

    private handleEnd = (): void => {
        this.videoControlChange.emit({
            type: "ended",
            element: this.videoElement
        });
        this.isEnded = true;
    };

    private handleVolumeChange = (): void => {
        this.videoControlChange.emit({
            type: "volumechange",
            element: this.videoElement
        });
    };

    connectedCallback() {
        this.videoElement = this.element.querySelector("video") as HTMLVideoElement;
        if (this.videoElement === null) {
            return;
        }
        if (this.videoElement.hasAttribute("autoplay")) {
            this.videoElement.removeAttribute("autoplay");
            this.videoElement.setAttribute(this.autoplayAttr, "");
        }
        this.videoElement.addEventListener("loadedmetadata", this.handleMetadata);
        this.videoElement.addEventListener("play", this.handlePlay);
        this.videoElement.addEventListener("pause", this.handlePause);
        this.videoElement.addEventListener("ended", this.handleEnd);
        this.videoElement.addEventListener("volumechange", this.handleVolumeChange);
        if (this.isInitialized) {
            return;
        }
        this.hasOwnPoster = !!this.videoElement.getAttribute("poster");
        if (this.videoElement.getAttribute("controls") !== null && this.videoElement.hasAttribute("controls")) {
            this.hasVideoControls = true;
            this.videoElement.removeAttribute("controls");
        } else if (!this.videoElement.hasAttribute("controls") && !this.videoElement.hasAttribute(this.autoplayAttr)) {
            this.hasVideoControls = true;
        }
        this.isInitialized = true;
    }

    componentDidLoad() {
        this.contentObserver.observe(this.videoWrapper, {
            attributes: true
        });
        if (this.videoElement.hasAttribute(this.autoplayAttr)) {
            if (this.hasVideoControls && !this.haveControlsBeenSet) {
                this.videoElement.setAttribute("controls", "");
                this.haveControlsBeenSet = true;
            } else if (!this.hasVideoControls && this.videoElement.hasAttribute("loop")) {
                this.hasLoopAutoplay = true;
                if (this.videoElement.hasAttribute("muted")) {
                    this.videoElement.muted = true;
                }
            } else if (!this.hasVideoControls) {
                this.hasAutoplayNoControls = true;
            }
        }
        this.isPaused = this.videoElement.paused;
        this.intersectionObserver.observe(this.element);
    }

    disconnectedCallback() {
        if (this.videoElement === null) {
            return;
        }
        this.videoElement.removeEventListener("loadedmetadata", this.handleMetadata);
        this.videoElement.removeEventListener("play", this.handlePlay);
        this.videoElement.removeEventListener("pause", this.handlePause);
        this.videoElement.removeEventListener("ended", this.handleEnd);
        this.videoElement.removeEventListener("volumechange", this.handleVolumeChange);
    }

    render() {
        const { baseClass, isPaused, hasLoopAutoplay, hasAutoplayNoControls, noCustomPlayButton, haveControlsBeenSet } = this;

        return (
            <Host class={baseClass}>
                {/* @ts-expect-error Check for typing */}
                <div class={`${baseClass}__video-wrapper`} ref={(el) => (this.videoWrapper = el)}>
                    <slot />
                    {!noCustomPlayButton && (
                        <button
                            class={{
                                [`${baseClass}__play-button`]: true,
                                [`${baseClass}__play-button--inactive`]: !isPaused || hasLoopAutoplay || hasAutoplayNoControls,
                                [`${baseClass}__play-button--controls-active`]: haveControlsBeenSet
                            }}
                            type="button"
                            onClick={() => this.videoElement.play()}
                            aria-hidden={isPaused ? "false" : "true"}
                            aria-label="play"
                            name="play"
                        >
                            <ux-icon class={`${baseClass}__play-icon`} name="play" weight="bold" filled />
                        </button>
                    )}
                </div>
            </Host>
        );
    }
}
